import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import {
	AppModalService,
	ModalData,
} from '@datachain/ui-sdk/components/app-modal';
import { AppPanelComponent } from '@datachain/ui-sdk/components/app-panel';
import { ButtonToggle } from '@datachain/ui-sdk/components/app-toggle-buttons';
import { Store } from '@ngrx/store';
import { Map } from 'immutable';
import {
	BehaviorSubject,
	firstValueFrom,
	map,
	Observable,
	takeUntil,
	tap,
	withLatestFrom,
} from 'rxjs';

import { DcIcons } from '../../../../ui/app-dc.icons';
import {
	AppMemberListComponent,
	MemberListGridOptions,
} from '../../../../ui/components/app-member-list/app-member-list.component';
import {
	MemberItemEntity,
	SelectedMember,
} from '../../../../ui/components/app-member-list/member-item.entity';
import { MemberListEntity } from '../../../../ui/components/app-member-list/member-list.entity';
import { ExpositionsSelector, getEligibleConsumers } from '../../../store';
import { ConsumerListProvider, ConsumerType } from './consumer-list.provider';

@Component({
	selector: 'app-consumer-mapping-popup',
	templateUrl: './consumer-mapping-popup.component.html',
	styleUrls: ['./consumer-mapping-popup.component.scss'],
	providers: [ConsumerListProvider],
})
export class ConsumerMappingPopupComponent<
		T extends { selectedConsumers: Map<number, SelectedMember> }
	>
	extends DcBaseComponent
	implements OnInit
{
	protected readonly DcIcons = DcIcons;
	protected readonly ConsumerType = ConsumerType;

	public consumerTypes: Array<ButtonToggle> = [
		{
			label: $localize`:i18n=@@general.memberType.users:`,
			id: 'get-users',
			isSelected: true,
			icon: DcIcons.User,
			cb: (): void => this.consumerType$.next(ConsumerType.User),
		},
		{
			label: $localize`:i18n=@@general.memberType.groups:`,
			id: 'get-groups',
			isSelected: false,
			icon: DcIcons.Group,
			cb: (): void => this.consumerType$.next(ConsumerType.Group),
		},
	];

	public vo$: Observable<{
		consumerList: MemberListEntity;
		selectedConsumers: Array<SelectedMember>;
		initialSelectedConsumers: Map<number, SelectedMember>;
	}>;

	public consumerType$ = new BehaviorSubject<ConsumerType>(ConsumerType.User);

	private readonly selectedConsumers$ = new BehaviorSubject<
		Array<SelectedMember>
	>(this.modalData.selectedConsumers.toList().toArray());

	private readonly initialSelectedConsumers$ = new BehaviorSubject<
		Map<number, SelectedMember>
	>(this.modalData.selectedConsumers);

	@ViewChild(AppPanelComponent)
	private readonly appPanelCmp: AppPanelComponent | null = null;

	@ViewChild(AppMemberListComponent)
	private readonly appMemberListCmp: AppMemberListComponent | null = null;

	public constructor(
		private readonly store: Store,
		private readonly selector: ExpositionsSelector,
		private readonly modalService: AppModalService,
		@Inject(MAT_DIALOG_DATA) public readonly modalData: ModalData & T,
		private readonly consumerListProvider: ConsumerListProvider
	) {
		super();
		this.cmpId = 'consumer-mapping-popup';

		const consumerList$ = this.selector.getEligibleConsumers$().pipe(
			withLatestFrom(
				this.consumerType$,
				this.selector.getTotalEligibleConsumers$()
			),
			map(([elements, consumerType, total]) => {
				const items = elements.map((item) =>
					MemberItemEntity.buildMember({
						id: item.id,
						creationDate: item.creationDate,
						updateDate: item.updateDate,
						login: item.login,
						firstName: item.firstName,
						lastName: item.lastName,
						mail: item.mail,
						avatar: item.avatar,
						name: item.name,
						description: item.description,
						isGroup: item.isGroup,
					})
				);
				return this.consumerListProvider.setListElements(
					consumerType,
					items,
					total
				);
			})
		);

		this.vo$ = combineAll({
			consumerList: consumerList$,
			selectedConsumers: this.selectedConsumers$,
			initialSelectedConsumers: this.initialSelectedConsumers$,
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();
		this.handleSubscription();
	}

	public async onOptionChange(option: MemberListGridOptions): Promise<void> {
		const consumerType = await firstValueFrom(this.consumerType$);

		if (consumerType === ConsumerType.User) {
			this.store.dispatch(
				getEligibleConsumers({
					consumerType,
					page: option.page,
					searchTerm: option.searchTerm,
					filterFields: option.filterFields,
					sortFields: option.sortFields,
				})
			);
			return;
		}
		this.store.dispatch(
			getEligibleConsumers({
				consumerType,
				page: option.page,
				searchTerm: option.searchTerm,
				filterFields: option.filterFields,
				sortFields: option.sortFields,
			})
		);
	}

	public selectTargetConsumers(selectedMembers: Array<SelectedMember>): void {
		this.selectedConsumers$.next(selectedMembers);
	}

	public async removeSelectedConsumer(id: number): Promise<void> {
		if (!this.appMemberListCmp) {
			return;
		}
		await this.appMemberListCmp.removeSelectedMember(id);
	}

	public async saveAction(): Promise<void> {
		const selectedConsumers = await firstValueFrom(this.selectedConsumers$);

		this.modalService.closeActiveModal({
			selectedConsumers: Map(selectedConsumers.map((item) => [item.id, item])),
		});
	}

	public refresh(): void {
		if (!this.appMemberListCmp) {
			return;
		}
		this.appMemberListCmp.refresh();
	}

	public search(searchTerm: string): void {
		if (!this.appMemberListCmp) {
			return;
		}
		this.appMemberListCmp.search(searchTerm);
	}

	private handleSubscription(): void {
		this.consumerType$
			.pipe(
				takeUntil(this.onDestroy$),
				tap(() => {
					if (this.appPanelCmp) {
						// this.appPanelCmp.clearSearch(new MouseEvent('click'));
					}

					this.appMemberListCmp?.resetGrid();
				})
			)
			.subscribe();
	}
}
